import { useState, useEffect, useCallback } from 'react';

// Custom hook to handle PWA install prompt
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    const checkInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return true;
      }
      // iOS Safari check
      if (window.navigator.standalone === true) {
        setIsInstalled(true);
        return true;
      }
      return false;
    };

    if (checkInstalled()) return;

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      localStorage.setItem('pwa_installed', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) {
      return { outcome: 'unavailable' };
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        localStorage.setItem('pwa_installed', 'true');
      }
      
      setDeferredPrompt(null);
      setIsInstallable(false);
      
      return { outcome };
    } catch (error) {
      console.error('Install prompt error:', error);
      return { outcome: 'error', error };
    }
  }, [deferredPrompt]);

  const dismissPrompt = useCallback(() => {
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  }, []);

  const shouldShowPrompt = useCallback((isFirstLogin = false) => {
    // Don't show if already installed
    if (isInstalled || localStorage.getItem('pwa_installed')) {
      return false;
    }

    // Don't show if not installable
    if (!isInstallable) {
      return false;
    }

    // For first login, always show
    if (isFirstLogin) {
      return true;
    }

    // Check if dismissed recently (within 7 days)
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < sevenDays) {
        return false;
      }
    }

    return true;
  }, [isInstallable, isInstalled]);

  // iOS specific instructions check
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const showIOSInstructions = isIOS && isSafari && !isInstalled;

  return {
    isInstallable,
    isInstalled,
    promptInstall,
    dismissPrompt,
    shouldShowPrompt,
    showIOSInstructions,
    isIOS
  };
};

export default usePWAInstall;
